import { useState, useCallback, useRef } from 'react';
import { useImportExport } from '@/lib/hooks/crm/useImportExport';
import { CrmFieldMapping } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'contacts' | 'companies' | 'deals';
  onImportComplete?: () => void;
}

const ENTITY_FIELDS: Record<string, string[]> = {
  contacts: [
    'name',
    'email',
    'phone',
    'title',
    'department',
    'lifecycle_stage',
    'source',
    'tags',
    'notes',
  ],
  companies: ['name', 'domain', 'industry', 'size', 'phone', 'email', 'website', 'tags', 'notes'],
  deals: ['name', 'value', 'currency', 'expected_close_date', 'tags', 'notes'],
};

const SKIP_VALUE = '__skip__';

export function ImportDialog({
  open,
  onOpenChange,
  entityType,
  onImportComplete,
}: ImportDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [, setCsvText] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string | null>>({});
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { preview, importResult, loading, previewImport, executeImport, reset } = useImportExport();

  const availableFields = ENTITY_FIELDS[entityType] || [];

  const handleFileRead = useCallback(
    async (file: File) => {
      setFileError(null);
      if (!file.name.endsWith('.csv')) {
        setFileError('Please select a CSV file.');
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        setCsvText(text);
        const result = await previewImport(entityType, text);
        if (result) {
          const initialMappings: Record<string, string | null> = {};
          result.mappedFields.forEach((mf: CrmFieldMapping) => {
            initialMappings[mf.csvHeader] = mf.crmField;
          });
          setFieldMappings(initialMappings);
          setStep(2);
        } else {
          setFileError('Failed to parse CSV file.');
        }
      };
      reader.onerror = () => {
        setFileError('Failed to read file.');
      };
      reader.readAsText(file);
    },
    [entityType, previewImport],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileRead(file);
    },
    [handleFileRead],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileRead(file);
    },
    [handleFileRead],
  );

  const handleMappingChange = useCallback((csvHeader: string, value: string) => {
    setFieldMappings((prev) => ({
      ...prev,
      [csvHeader]: value === SKIP_VALUE ? null : value,
    }));
  }, []);

  const handleExecute = useCallback(async () => {
    if (!preview) return;
    const mapping: Record<string, string> = {};
    for (const [csvHeader, crmField] of Object.entries(fieldMappings)) {
      if (crmField) {
        mapping[csvHeader] = crmField;
      }
    }
    await executeImport(entityType, preview.previewRows, mapping);
  }, [entityType, preview, fieldMappings, executeImport]);

  const handleClose = useCallback(
    (openState: boolean) => {
      if (!openState) {
        const hadImports = importResult && importResult.successCount > 0;
        if (hadImports) {
          onImportComplete?.();
        }
        reset();
        setStep(1);
        setCsvText(null);
        setFileName(null);
        setFieldMappings({});
        setFileError(null);
      }
      onOpenChange(openState);
    },
    [importResult, onImportComplete, onOpenChange, reset],
  );

  const handleDone = useCallback(() => {
    if (importResult && importResult.successCount > 0) {
      onImportComplete?.();
    }
    reset();
    setStep(1);
    setCsvText(null);
    setFileName(null);
    setFieldMappings({});
    setFileError(null);
    onOpenChange(false);
  }, [importResult, onImportComplete, onOpenChange, reset]);

  const mappedFieldCount = Object.values(fieldMappings).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Upload a CSV file to import data.'}
            {step === 2 && 'Map CSV columns to CRM fields.'}
            {step === 3 && 'Review and execute the import.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">Drag and drop a CSV file here</p>
              <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileInput}
              />
            </div>
            {loading && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                Parsing CSV...
              </div>
            )}
            {fileError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {fileError}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Map Fields */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="secondary">{preview.totalRows} rows found</Badge>
              {fileName && <span className="text-xs text-muted-foreground">{fileName}</span>}
            </div>

            {preview.errors.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive space-y-1">
                {preview.errors.map((err, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{err}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium">CSV Column</th>
                    <th className="text-left p-2 font-medium">Sample Values</th>
                    <th className="text-left p-2 font-medium">Map To</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.mappedFields.map((mf: CrmFieldMapping) => (
                    <tr key={mf.csvHeader} className="border-b last:border-b-0">
                      <td className="p-2 font-mono text-xs">{mf.csvHeader}</td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {mf.sampleValues.slice(0, 2).join(', ')}
                      </td>
                      <td className="p-2">
                        <Select
                          value={fieldMappings[mf.csvHeader] ?? SKIP_VALUE}
                          onValueChange={(val) => handleMappingChange(mf.csvHeader, val)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SKIP_VALUE}>
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <X className="h-3 w-3" /> Skip
                              </span>
                            </SelectItem>
                            {availableFields.map((field) => (
                              <SelectItem key={field} value={field}>
                                {field.replace(/_/g, ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setCsvText(null);
                  setFileName(null);
                  setFieldMappings({});
                }}
              >
                Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={mappedFieldCount === 0}>
                Next
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Execute & Results */}
        {step === 3 && preview && (
          <div className="space-y-4">
            {!importResult && (
              <>
                <div className="rounded-md bg-muted p-4 space-y-2 text-sm">
                  <p>
                    <strong>{preview.totalRows}</strong> rows to import
                  </p>
                  <p>
                    <strong>{mappedFieldCount}</strong> fields mapped
                  </p>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setStep(2)} disabled={loading}>
                    Back
                  </Button>
                  <Button onClick={handleExecute} disabled={loading}>
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                        Importing...
                      </span>
                    ) : (
                      'Import'
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}

            {importResult && (
              <>
                <div className="rounded-md border p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    {importResult.errorCount === 0 ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                    )}
                    <span className="font-medium">Import Complete</span>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                      {importResult.successCount} succeeded
                    </span>
                    {importResult.errorCount > 0 && (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                        {importResult.errorCount} failed
                      </span>
                    )}
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="max-h-40 overflow-y-auto rounded bg-destructive/5 p-2 space-y-1">
                      {importResult.errors.map((err, i) => (
                        <div key={i} className="text-xs text-destructive">
                          Row {err.row}: {err.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button onClick={handleDone}>Done</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
